import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { useRecordExport } from '@/hooks/useTimeTrackingAudit';
import { useTimeEntries } from '@/hooks/useTimeTracking';
import { api } from '@/lib/apiClient';
import {
  Download,
  FileSpreadsheet,
  FileText,
  Loader2,
  Calendar,
  Users,
  Building2,
  Shield,
} from 'lucide-react';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

type ExportType = 'time_entries' | 'daily_summary' | 'monthly_report' | 'full_audit';
type ExportFormat = 'xlsx' | 'pdf' | 'csv';
type ExportPurpose = 'internal_audit' | 'labor_inspection' | 'employee_request';

const EXPORT_TYPES = [
  { value: 'time_entries', label: 'Registros de Fichaje', icon: Clock },
  { value: 'daily_summary', label: 'Resumen Diario', icon: Calendar },
  { value: 'monthly_report', label: 'Informe Mensual', icon: FileText },
  { value: 'full_audit', label: 'Auditoría Completa', icon: Shield },
];

const EXPORT_PURPOSES = [
  { value: 'internal_audit', label: 'Auditoría Interna' },
  { value: 'labor_inspection', label: 'Inspección de Trabajo' },
  { value: 'employee_request', label: 'Solicitud del Empleado' },
];

import { Clock } from 'lucide-react';

export function TimeTrackingExport() {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [exportType, setExportType] = useState<ExportType>('monthly_report');
  const [exportFormat, setExportFormat] = useState<ExportFormat>('xlsx');
  const [exportPurpose, setExportPurpose] = useState<ExportPurpose>('internal_audit');
  const [dateFrom, setDateFrom] = useState(format(startOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(format(endOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd'));
  const [includeSignatures, setIncludeSignatures] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  const recordExportMutation = useRecordExport();

  const fetchDataForExport = async () => {
    const { data: entries, error: entriesError } = await api
      .from('time_entries')
      .select(`
        *,
        user:user_id (id, full_name, employee_id, area)
      `)
      .gte('event_time', `${dateFrom}T00:00:00`)
      .lte('event_time', `${dateTo}T23:59:59`)
      .order('event_time', { ascending: true });

    if (entriesError) throw entriesError;

    const { data: dailySummary, error: summaryError } = await api
      .from('attendance_daily_summary')
      .select('*')
      .gte('work_date', dateFrom)
      .lte('work_date', dateTo);

    if (summaryError) throw summaryError;

    const { data: profiles, error: profilesError } = await api
      .from('profiles')
      .select('id, full_name, employee_id, area')
      .eq('is_active', true);

    if (profilesError) throw profilesError;

    let signatures = [];
    if (includeSignatures) {
      const { data: sigs, error: sigsError } = await api
        .from('time_entry_signatures')
        .select('*')
        .gte('period_start', dateFrom)
        .lte('period_end', dateTo);

      if (!sigsError) signatures = sigs || [];
    }

    return { entries, dailySummary, profiles, signatures };
  };

  const generateExcel = async (data: any) => {
    const wb = XLSX.utils.book_new();

    if (exportType === 'time_entries' || exportType === 'full_audit') {
      const entriesData = (data.entries || []).map((e: any) => ({
        'Fecha': format(new Date(e.event_time), 'dd/MM/yyyy'),
        'Hora': format(new Date(e.event_time), 'HH:mm:ss'),
        'Empleado': e.user?.full_name || '-',
        'ID Empleado': e.user?.employee_id || '-',
        'Área': e.user?.area || '-',
        'Tipo Evento': e.event_type === 'clock_in' ? 'Entrada' :
                       e.event_type === 'clock_out' ? 'Salida' :
                       e.event_type === 'break_start' ? 'Inicio Pausa' : 'Fin Pausa',
        'Motivo Pausa': e.break_reason || '-',
        'Manual': e.is_manual ? 'Sí' : 'No',
        'Notas': e.notes || '-',
      }));

      const wsEntries = XLSX.utils.json_to_sheet(entriesData);
      XLSX.utils.book_append_sheet(wb, wsEntries, 'Fichajes');
    }

    if (exportType === 'daily_summary' || exportType === 'monthly_report' || exportType === 'full_audit') {
      const profileMap = new Map((data.profiles || []).map((p: any) => [p.id, p]));

      const summaryData = (data.dailySummary || []).map((s: any) => {
        const profile = profileMap.get(s.user_id);
        return {
          'Fecha': format(new Date(s.work_date), 'dd/MM/yyyy'),
          'Empleado': profile?.full_name || '-',
          'ID Empleado': profile?.employee_id || '-',
          'Primera Entrada': s.first_clock_in ? format(new Date(s.first_clock_in), 'HH:mm') : '-',
          'Última Salida': s.last_clock_out ? format(new Date(s.last_clock_out), 'HH:mm') : '-',
          'Horas Trabajadas': (s.hours_worked || 0).toFixed(2),
          'Minutos Pausa': s.pause_minutes || 0,
        };
      });

      const wsSummary = XLSX.utils.json_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Resumen Diario');
    }

    if (includeSignatures && data.signatures?.length > 0) {
      const profileMap = new Map((data.profiles || []).map((p: any) => [p.id, p]));

      const signaturesData = data.signatures.map((s: any) => {
        const profile = profileMap.get(s.user_id);
        return {
          'Empleado': profile?.full_name || '-',
          'ID Empleado': profile?.employee_id || '-',
          'Período Inicio': format(new Date(s.period_start), 'dd/MM/yyyy'),
          'Período Fin': format(new Date(s.period_end), 'dd/MM/yyyy'),
          'Horas Totales': s.total_hours_worked?.toFixed(2) || '0',
          'Días Trabajados': s.total_days_worked || 0,
          'Estado': s.status === 'signed' ? 'Firmado' :
                   s.status === 'disputed' ? 'Disputado' :
                   s.status === 'approved' ? 'Aprobado' : 'Pendiente',
          'Fecha Firma': s.signed_at ? format(new Date(s.signed_at), 'dd/MM/yyyy HH:mm') : '-',
          'Observaciones': s.employee_notes || '-',
        };
      });

      const wsSignatures = XLSX.utils.json_to_sheet(signaturesData);
      XLSX.utils.book_append_sheet(wb, wsSignatures, 'Firmas');
    }

    const metadata = [
      { 'Campo': 'Empresa', 'Valor': 'MAGMA Composites S.L.' },
      { 'Campo': 'CIF', 'Valor': 'B-XXXXXXXX' },
      { 'Campo': 'Período', 'Valor': `${format(new Date(dateFrom), 'dd/MM/yyyy')} - ${format(new Date(dateTo), 'dd/MM/yyyy')}` },
      { 'Campo': 'Fecha Generación', 'Valor': format(new Date(), 'dd/MM/yyyy HH:mm:ss') },
      { 'Campo': 'Propósito', 'Valor': EXPORT_PURPOSES.find(p => p.value === exportPurpose)?.label || '-' },
      { 'Campo': 'Normativa', 'Valor': 'RDL 8/2019 (Art. 34.9 ET)' },
    ];

    const wsMetadata = XLSX.utils.json_to_sheet(metadata);
    XLSX.utils.book_append_sheet(wb, wsMetadata, 'Información');

    const fileName = `fichajes_${exportType}_${format(new Date(dateFrom), 'yyyyMM')}.xlsx`;
    XLSX.writeFile(wb, fileName);

    return { fileName, recordCount: (data.entries?.length || 0) + (data.dailySummary?.length || 0) };
  };

  const generatePDF = async (data: any) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(18);
    doc.text('REGISTRO DE JORNADA LABORAL', pageWidth / 2, 20, { align: 'center' });

    doc.setFontSize(10);
    doc.text('Conforme al Art. 34.9 del Estatuto de los Trabajadores (RDL 8/2019)', pageWidth / 2, 28, { align: 'center' });

    doc.setFontSize(12);
    doc.text('MAGMA Composites S.L.', 14, 45);
    doc.setFontSize(10);
    doc.text(`Período: ${format(new Date(dateFrom), 'dd/MM/yyyy')} - ${format(new Date(dateTo), 'dd/MM/yyyy')}`, 14, 52);
    doc.text(`Generado: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, 14, 58);
    doc.text(`Propósito: ${EXPORT_PURPOSES.find(p => p.value === exportPurpose)?.label || '-'}`, 14, 64);

    let yPos = 75;

    if (exportType === 'daily_summary' || exportType === 'monthly_report' || exportType === 'full_audit') {
      const profileMap = new Map((data.profiles || []).map((p: any) => [p.id, p]));

      const tableData = (data.dailySummary || []).map((s: any) => {
        const profile = profileMap.get(s.user_id);
        return [
          format(new Date(s.work_date), 'dd/MM/yyyy'),
          profile?.full_name || '-',
          profile?.employee_id || '-',
          s.first_clock_in ? format(new Date(s.first_clock_in), 'HH:mm') : '-',
          s.last_clock_out ? format(new Date(s.last_clock_out), 'HH:mm') : '-',
          `${(s.hours_worked || 0).toFixed(2)}h`,
        ];
      });

      autoTable(doc, {
        startY: yPos,
        head: [['Fecha', 'Empleado', 'ID', 'Entrada', 'Salida', 'Horas']],
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [59, 130, 246] },
        styles: { fontSize: 8 },
      });

      yPos = (doc as any).lastAutoTable.finalY + 15;
    }

    if (includeSignatures && data.signatures?.length > 0) {
      const profileMap = new Map((data.profiles || []).map((p: any) => [p.id, p]));

      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(12);
      doc.text('FIRMAS DE CONFORMIDAD', 14, yPos);
      yPos += 10;

      const sigTableData = data.signatures.map((s: any) => {
        const profile = profileMap.get(s.user_id);
        return [
          profile?.full_name || '-',
          `${format(new Date(s.period_start), 'dd/MM')} - ${format(new Date(s.period_end), 'dd/MM/yyyy')}`,
          `${s.total_hours_worked?.toFixed(2) || '0'}h`,
          s.status === 'signed' ? 'FIRMADO' : s.status,
          s.signed_at ? format(new Date(s.signed_at), 'dd/MM/yyyy') : '-',
        ];
      });

      autoTable(doc, {
        startY: yPos,
        head: [['Empleado', 'Período', 'Horas', 'Estado', 'Fecha Firma']],
        body: sigTableData,
        theme: 'grid',
        headStyles: { fillColor: [34, 197, 94] },
        styles: { fontSize: 8 },
      });
    }

    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(
        `Página ${i} de ${pageCount} - Documento generado automáticamente - Conservar 4 años (RDL 8/2019)`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }

    const fileName = `fichajes_${exportType}_${format(new Date(dateFrom), 'yyyyMM')}.pdf`;
    doc.save(fileName);

    return { fileName, recordCount: (data.dailySummary?.length || 0) };
  };

  const handleExport = async () => {
    setIsExporting(true);

    try {
      const data = await fetchDataForExport();

      let result;
      if (exportFormat === 'xlsx' || exportFormat === 'csv') {
        result = await generateExcel(data);
      } else {
        result = await generatePDF(data);
      }

      await recordExportMutation.mutateAsync({
        exportType,
        format: exportFormat,
        dateFrom,
        dateTo,
        fileName: result.fileName,
        recordCount: result.recordCount,
        purpose: exportPurpose,
      });

      toast({
        title: 'Exportación completada',
        description: `Archivo ${result.fileName} generado correctamente`,
      });

      setDialogOpen(false);
    } catch (error: any) {
      toast({
        title: 'Error en exportación',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Download className="w-4 h-4 mr-2" />
          Exportar
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="w-5 h-5" />
            Exportar Registro de Fichajes
          </DialogTitle>
          <DialogDescription>
            Genera informes oficiales conforme a RDL 8/2019
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Desde</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Hasta</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Tipo de Informe</Label>
            <Select value={exportType} onValueChange={(v: ExportType) => setExportType(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EXPORT_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex items-center gap-2">
                      <type.icon className="w-4 h-4" />
                      {type.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Formato</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={exportFormat === 'xlsx' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setExportFormat('xlsx')}
                className="flex-1"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Excel
              </Button>
              <Button
                type="button"
                variant={exportFormat === 'pdf' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setExportFormat('pdf')}
                className="flex-1"
              >
                <FileText className="w-4 h-4 mr-2" />
                PDF
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Propósito</Label>
            <Select value={exportPurpose} onValueChange={(v: ExportPurpose) => setExportPurpose(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EXPORT_PURPOSES.map((purpose) => (
                  <SelectItem key={purpose.value} value={purpose.value}>
                    {purpose.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="include-signatures"
              checked={includeSignatures}
              onCheckedChange={(checked) => setIncludeSignatures(checked as boolean)}
            />
            <label htmlFor="include-signatures" className="text-sm cursor-pointer">
              Incluir firmas de conformidad
            </label>
          </div>

          <Card className="bg-muted/50">
            <CardContent className="pt-4">
              <div className="flex items-start gap-2 text-sm">
                <Shield className="w-4 h-4 mt-0.5 text-primary" />
                <div>
                  <p className="font-medium">Registro oficial de auditoría</p>
                  <p className="text-muted-foreground text-xs mt-1">
                    Esta exportación quedará registrada en el sistema de auditoría
                    conforme a los requisitos de RDL 8/2019.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setDialogOpen(false)}>
            Cancelar
          </Button>
          <Button onClick={handleExport} disabled={isExporting}>
            {isExporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Exportar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default TimeTrackingExport;
